/**
 * Plans View Component
 * Displays available subscription plans and top-up options
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, type PlansResponse } from '../services/apiClient';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { formatNumber, formatCurrency } from '../utils/persian';

export const PlansView: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PlansResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getPlans();
      setPlans(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'خطا در بارگذاری پلن‌ها';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (planId: string) => {
    navigate(`/purchase?plan_id=${planId}`);
  };

  const getTierBadgeStyle = (tier: string): React.CSSProperties => {
    const colors: Record<string, { bg: string; text: string }> = {
      'free': { bg: '#e0e0e0', text: '#666' },
      'basic': { bg: '#e3f2fd', text: '#1976d2' },
      'standard': { bg: '#e8f5e9', text: '#388e3c' },
      'premium': { bg: '#fff3e0', text: '#f57c00' },
      'enterprise': { bg: '#f3e5f5', text: '#7b1fa2' },
    };
    const color = colors[tier.toLowerCase()] || colors['basic'];
    return {
      ...styles.tierBadge,
      backgroundColor: color.bg,
      color: color.text,
    };
  };

  if (loading) {
    return <LoadingSpinner message="در حال بارگذاری پلن‌ها..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadPlans} />;
  }

  if (!plans) {
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>پلن‌های اشتراک</h2>
        <p style={styles.subtitle}>
          پلن مناسب خود را انتخاب کنید
        </p>
      </div>

      {/* Subscription Plans */}
      <div style={styles.plansList}>
        {plans.plans.map((plan) => {
            const pricePerCredit = plan.price_per_credit_toman;
            
            return (
              <div key={plan.id} style={styles.planCard}>
                <div style={styles.planHeader}>
                  <div>
                    <h3 style={styles.planName}>{plan.name_fa || plan.name}</h3>
                    <span style={getTierBadgeStyle(plan.tier)}>{plan.tier}</span>
                  </div>
                  <div style={styles.planPrice}>
                    <p style={styles.priceAmount}>{formatCurrency(plan.price_toman)}</p>
                  </div>
                </div>

                <div style={styles.planCredits}>
                  <p style={styles.creditsAmount}>{formatNumber(plan.credits)}</p>
                  <p style={styles.creditsLabel}>واحد اعتبار</p>
                </div>

                <div style={styles.planFeatures}>
                  <div style={styles.featureItem}>
                    <span style={styles.featureIcon}>
                      {plan.features.ai_features_enabled ? '✅' : '❌'}
                    </span>
                    <span style={styles.featureText}>
                      ویژگی‌های هوش مصنوعی
                    </span>
                  </div>

                  <div style={styles.featureItem}>
                    <span style={styles.featureIcon}>⚡</span>
                    <span style={styles.featureText}>
                      {formatNumber(plan.features.requests_per_hour)} درخواست در ساعت
                    </span>
                  </div>

                  <div style={styles.featureItem}>
                    <span style={styles.featureIcon}>🔄</span>
                    <span style={styles.featureText}>
                      {formatNumber(plan.features.concurrent_jobs)} کار همزمان
                    </span>
                  </div>

                  <div style={styles.featureItem}>
                    <span style={styles.featureIcon}>
                      {plan.features.priority_processing ? '🚀' : '⏱️'}
                    </span>
                    <span style={styles.featureText}>
                      {plan.features.priority_processing ? 'پردازش اولویت‌دار' : 'پردازش عادی'}
                    </span>
                  </div>
                </div>

                <div style={styles.planFooter}>
                  <p style={styles.pricePerCredit}>
                    {formatNumber(Math.round(pricePerCredit))} تومان به ازای هر واحد
                  </p>
                  <button
                    onClick={() => handleSelectPlan(plan.id)}
                    style={styles.selectButton}
                  >
                    انتخاب پلن
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {/* Top-up Options */}
      {plans.top_up_options && plans.top_up_options.length > 0 && (
        <>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>شارژ اعتبار</h3>
            <p style={styles.sectionSubtitle}>
              بدون نیاز به اشتراک، اعتبار خریداری کنید
            </p>
          </div>

          <div style={styles.topUpList}>
            {plans.top_up_options.map((option) => {
              const pricePerCredit = option.price_per_credit_toman;
              
              return (
                <div key={`${option.credits}-${option.price_toman}`} style={styles.topUpCard}>
                  <div style={styles.topUpInfo}>
                    <p style={styles.topUpCredits}>{formatNumber(option.credits)}</p>
                    <p style={styles.topUpLabel}>واحد اعتبار</p>
                  </div>
                  <div style={styles.topUpPrice}>
                    <p style={styles.topUpAmount}>{formatCurrency(option.price_toman)}</p>
                    <p style={styles.topUpPerCredit}>
                      {formatNumber(Math.round(pricePerCredit))} تومان / واحد
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/purchase?credits=${option.credits}&type=top_up`)}
                    style={styles.topUpButton}
                  >
                    خرید
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    paddingBottom: '1rem',
  },
  header: {
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 0.25rem 0',
  },
  subtitle: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
  },
  plansList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginBottom: '2rem',
  },
  planCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '1.5rem',
  },
  planHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem',
  },
  planName: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 0.5rem 0',
  },
  tierBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  planPrice: {
    textAlign: 'left',
  },
  priceAmount: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: 0,
  },
  planCredits: {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '1rem',
    textAlign: 'center',
    marginBottom: '1rem',
  },
  creditsAmount: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#646cff',
    margin: '0 0 0.25rem 0',
  },
  creditsLabel: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
  },
  planFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  featureIcon: {
    fontSize: '1.25rem',
  },
  featureText: {
    fontSize: '0.875rem',
    color: '#666',
  },
  planFooter: {
    borderTop: '1px solid #f0f0f0',
    paddingTop: '1rem',
  },
  pricePerCredit: {
    fontSize: '0.75rem',
    color: '#999',
    margin: '0 0 0.75rem 0',
    textAlign: 'center',
  },
  selectButton: {
    width: '100%',
    backgroundColor: '#646cff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  sectionHeader: {
    marginBottom: '1rem',
  },
  sectionTitle: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 0.25rem 0',
  },
  sectionSubtitle: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
  },
  topUpList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  topUpCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  topUpInfo: {
    flex: 1,
  },
  topUpCredits: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 0.25rem 0',
  },
  topUpLabel: {
    fontSize: '0.75rem',
    color: '#666',
    margin: 0,
  },
  topUpPrice: {
    flex: 1,
    textAlign: 'center',
  },
  topUpAmount: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: '0 0 0.25rem 0',
  },
  topUpPerCredit: {
    fontSize: '0.625rem',
    color: '#999',
    margin: 0,
  },
  topUpButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5rem 1.5rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap',
  },
};
